import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ResultChatView, ResultErrorState, ResultLoadingState } from "@/components/result";
import { routes, strings } from "@/constants";
import { CodeLayoutVariantEnum, LanguageEnum, ResultPageStatusEnum } from "@/enums";
import { useAuth, useExamPolling } from "@/hooks";
import type { ChatResultInterface } from "@/interfaces";
import { CodeLayout } from "@/layouts";
import type { ResultPageStatusType } from "@/types";
import { formatRecommendedJobsToMarkdownHandler, stripMarkdownHandler } from "@/utils";

const buildChatResultHandler = (
    fromState: ChatResultInterface | null,
    polledResult: ReturnType<typeof useExamPolling>["result"],
    fallbackUserName: string,
): ChatResultInterface | null => {
    if (fromState) return fromState;

    if (!polledResult) return null;

    const {
        code,
        recommendedJobs,
    } = polledResult;

    return {
        code: code,
        content: formatRecommendedJobsToMarkdownHandler(recommendedJobs),
        id: crypto.randomUUID(),
        recommendedJobs: recommendedJobs,
        userName: fallbackUserName,
    };
};

export const ResultPage = () => {
    const [isCopied, setIsCopied] = useState(false);

    const location = useLocation();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const { user } = useAuth();

    const {
        errorMessage,
        onPollExisting: pollExistingHandler,
        onReset: resetPollingHandler,
        result: polledResult,
        status: pollingStatus,
    } = useExamPolling();

    const initialResult = (location.state as { result?: ChatResultInterface } | null)?.result || null;

    const hasFetchedRef = useRef(!!initialResult);

    const langParam = searchParams.get(strings.queryParams.lang);

    const lang = langParam === LanguageEnum.AR ? LanguageEnum.AR : LanguageEnum.EN;

    const jobId = searchParams.get(strings.queryParams.jobId);

    const code = searchParams.get(strings.queryParams.code) || "";

    const hasValidParams = !!jobId && !!code;

    const status: ResultPageStatusType = initialResult || pollingStatus === "success"
        ? ResultPageStatusEnum.SUCCESS
        : pollingStatus === "error"
            ? ResultPageStatusEnum.ERROR
            : ResultPageStatusEnum.LOADING;

    const layoutVariant = status === ResultPageStatusEnum.ERROR ? CodeLayoutVariantEnum.ERROR : CodeLayoutVariantEnum.DEFAULT;

    const result = buildChatResultHandler(
        initialResult,
        polledResult,
        user?.name || strings.code.defaultUserName,
    );

    const copyHandler = () => {
        if (!result) return;

        navigator.clipboard.writeText(stripMarkdownHandler(result.content));

        setIsCopied(true);

        setTimeout(
            () => setIsCopied(false),
            4000,
        );
    };

    const resetHandler = () => {
        resetPollingHandler();

        navigate(routes.code);
    };

    const downloadHandler = async () => {
        if (!result) return;

        const { downloadResultPdfHandler } = await import("@/utils/download-result-pdf");

        await downloadResultPdfHandler({
            code: result.code,
            jobs: result.recommendedJobs,
            lang,
            userName: result.userName,
        });
    };

    useEffect(
        () => {
            if (!hasValidParams) {
                navigate(routes.code);

                return;
            }

            if (hasFetchedRef.current) return;

            hasFetchedRef.current = true;

            pollExistingHandler({
                code,
                jobId: jobId as string,
                lang,
            });
        },
        [
            hasValidParams,
            jobId,
            code,
            navigate,
            lang,
            pollExistingHandler,
        ],
    );

    if (!hasValidParams) return null;

    if (status === ResultPageStatusEnum.LOADING) {
        return (
            <CodeLayout>
                <ResultLoadingState
                    code={code}
                    onReset={resetHandler}
                />
            </CodeLayout>
        );
    }

    if (status === ResultPageStatusEnum.ERROR) {
        return (
            <CodeLayout variant={layoutVariant}>
                <ResultErrorState
                    code={code}
                    errorMessage={errorMessage}
                    onReset={resetHandler}
                />
            </CodeLayout>
        );
    }

    if (!result) {
        return (
            <CodeLayout>
                <div className="flex items-center justify-center">
                    <span className="text-text-muted">{strings.result.loading}</span>
                </div>
            </CodeLayout>
        );
    }

    return (
        <CodeLayout isCentered={false}>
            <ResultChatView
                isCopied={isCopied}
                lang={lang}
                result={result}
                onCopy={copyHandler}
                onDownload={downloadHandler}
                onReset={resetHandler}
            />
        </CodeLayout>
    );
};
